import { useTheme } from '@automata/mui-theme';
import { useFlag } from '@automata/utils/hooks';
import Box from '@mui/material/Box';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup, {
  ToggleButtonGroupProps,
} from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import React from 'react';

import { selectSelectionPanel } from '../../../features/ui/selectors';
import {
  closeSelectionPanel,
  openSelectionPanel,
} from '../../../features/ui/uiSlice';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { NodeType } from '../../../types';
import { Icon } from '../../customIcons/misc';

type Props = {
  onOpenPanel: () => void;
};

export const BuildingBlocksToolbar = ({ onOpenPanel }: Props): JSX.Element => {
  const dispatch = useAppDispatch();
  const currentSelectionPanel = useAppSelector(selectSelectionPanel);

  const isConditionalTaskNodeEnabled = useFlag('fe.conditionals');

  const theme = useTheme();

  const handleToggleButtonGroupChange: ToggleButtonGroupProps['onChange'] = (
    _,
    value: NodeType
  ) => {
    if (currentSelectionPanel === value) {
      dispatch(closeSelectionPanel());
      return;
    }

    onOpenPanel();
    dispatch(closeSelectionPanel());
    dispatch(openSelectionPanel(value));
  };

  return (
    <Box boxShadow={3} borderRadius={1} bgcolor={theme.palette.common.white}>
      <ToggleButtonGroup
        orientation="vertical"
        onChange={handleToggleButtonGroupChange}
        exclusive
        size="medium"
      >
        <Tooltip title="Labware" placement="right">
          <ToggleButton
            value="labware"
            selected={currentSelectionPanel === NodeType.LABWARE}
          >
            <Icon iconType="labware" color="neutral" />
          </ToggleButton>
        </Tooltip>

        <Tooltip title="Tasks" placement="right">
          <ToggleButton
            value="task"
            selected={currentSelectionPanel === NodeType.TASK}
          >
            <Icon iconType="task" color="neutral" />
          </ToggleButton>
        </Tooltip>

        {isConditionalTaskNodeEnabled && (
          <Tooltip title="Conditional Flow" placement="right">
            <ToggleButton
              value="conditionalTask"
              selected={currentSelectionPanel === NodeType.CONDITIONAL_TASK}
            >
              <Icon iconType="conditionalflow" color="neutral" />
            </ToggleButton>
          </Tooltip>
        )}
      </ToggleButtonGroup>
    </Box>
  );
};
